import React, { useEffect } from 'react';
import { Redirect } from 'react-router-dom';
import { hasCookie, isLoggedIn } from '../../services/storage/GQStorage';

// Must stay in sync with gq_section_to_pascal_map in public/docs/index.html
const PASCAL_TO_SECTION = {
    Home: 'main_content',
    GettingStarted: 'getting_started',
    GenomeQuestHomePage: 'genomequest_home_page',
    SequenceDatabaseBrowser: 'sequence_database_browser',
    ResultBrowser: 'result_browser',
    IpSequenceSearching: 'ip_sequence_searching',
    KeywordSearching: 'keyword_searching',
    GeneralSequenceSearching: 'general_sequence_searching',
    AlignTwoSequences: 'align_two_sequences',
    GqDiscoverBrowser: 'gq_discover_browser',
    VariationDiscovery: 'variation_discovery',
    AntibodyModule: 'Antibody_Module',
    FullText: 'Full_text',
    ChemicalModification: 'chemical_modification',
    ContentUpdates: 'content_updates',
    ReleaseNotes: 'release_notes',
    GenomeQuestDatabases: 'genomequest_databases',
    SequenceFormats: 'sequence_formats',
    SequenceComparisonAlgorithms: 'sequence_comparison_algorithms',
    SupportedBrowsers: 'supported_browsers',
    Faq: 'faq',
    AppendixLegalStatusKindCodes: 'appendix_legal_status_kind_codes',
    AppendixOpenSourceNotifications: 'appendix_open_source_notifications',
};

const VALID_SECTIONS = new Set(Object.values(PASCAL_TO_SECTION));

const sanitizeSection = (section) => {
    if (!section || typeof section !== 'string') {
        return 'main_content';
    }
    return VALID_SECTIONS.has(section) ? section : 'main_content';
};

const sanitizeAnchor = (anchor) => {
    if (!anchor || typeof anchor !== 'string') {
        return '';
    }
    const trimmed = anchor.trim();
    return /^[A-Za-z0-9_:.\-]+$/.test(trimmed) ? trimmed : '';
};

const Documentation = ({ match }) => {
    const isReactLoggedIn = isLoggedIn();
    const hasPhpSession = hasCookie('PHPSESSID');
    const isAuthenticated = isReactLoggedIn || hasPhpSession;

    useEffect(() => {
        document.title = 'GenomeQuest - User Manual';
    }, []);

    useEffect(() => {
        if (!isAuthenticated) return;

        // Build docs URL from route params.
        // basePath = everything up to (and including) the slash before "docs/".
        // When pathname already contains /docs/ (pretty-URL form), slice there.
        // Otherwise (e.g. /fulltext/index.html#/docs entry), strip the trailing
        // filename so the app-mount prefix (/fulltext/) is preserved.
        const { origin, pathname } = window.location;
        const docsIdx = pathname.indexOf('/docs/');
        let basePath;
        if (docsIdx >= 0) {
            basePath = `${origin}${pathname.substring(0, docsIdx + 1)}`;
        } else {
            const pathBeforeHash = window.location.href.split('#')[0];
            basePath = pathBeforeHash.substring(0, pathBeforeHash.lastIndexOf('/') + 1);
        }

        const page = match && match.params && match.params.page;
        const anchor = sanitizeAnchor(match && match.params && match.params.anchor);

        let docsUrl;
        if (page === 'index.html' && anchor) {
            // Pretty-URL form: /docs/index.html/<PascalCase>
            // Resolve PascalCase → section slug, hand off to the docs bundle via ?s=.
            // The bundle will replaceState back to the pretty URL once loaded.
            const section = sanitizeSection(PASCAL_TO_SECTION[anchor]);
            docsUrl = `${basePath}docs/index.html?s=${encodeURIComponent(section)}`;
        } else if (page === 'index.html') {
            docsUrl = `${basePath}docs/index.html`;
        } else if (page) {
            // Legacy hyphen-slug form: /docs/<slug>[/<anchor>]
            const section = sanitizeSection(page.replace(/-/g, '_'));
            const anchorParam = anchor ? `&a=${encodeURIComponent(anchor)}` : '';
            docsUrl = `${basePath}docs/index.html?s=${encodeURIComponent(section)}${anchorParam}`;
        } else {
            docsUrl = `${basePath}docs/index.html`;
        }

        window.location.replace(docsUrl);
    }, [isAuthenticated, match]);

    // Not logged in → redirect to login
    if (!isAuthenticated) {
        return <Redirect to="/login" />;
    }

    return (
        <div style={{ width: '100%', height: 'calc(100vh - 120px)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <p>Loading documentation...</p>
        </div>
    );
};

export default Documentation;
